import { Component, OnInit, Input, ElementRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, FormControl, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Lead } from '../../../../../shared/lead';
import { PostLead } from '../../../../../shared/post-lead';
import { FormResponse } from '../../../../../shared/form-response';
import { leadValues } from '../../../../../services/lead-values.service';
import { FruHttpService } from '../../../../../services/fru-http.service';
import { JornayaFormService, JornayaNotification } from '../../../../../services/jornaya-form.service';
import { ConsentAttemptTelemetryService, ConsentAttemptEventOptions } from '../../../../../services/consent-attempt-telemetry.service';
import { formatCurrency } from '@angular/common';
@Component({
    selector: 'lead-form',
    templateUrl: './lead-form.component.html',
    styleUrls: ['./lead-form.component.scss'],
    standalone: false
})

export class LeadFormComponent implements OnInit {
  @Input() lead: Lead;
  @Input() consentUpdate: boolean = false;

  public leadForm: UntypedFormGroup;
  public leadValues: Object;
  public phoneMask: Object = {
    mask: '(#00) 000-0000',
    definitions: {'#': /[1-9]/},
    lazy: false
  }
  public zipMask: Object = {
    mask: '00000', 
    lazy: false
  }
  public disableSubmit: boolean = false;
  public buyingProcessControl;
  public veteranMilitaryControl;
  public zipCodeControl;
  public firstNameControl;
  public lastNameControl;
  public emailControl;
  public phoneControl;
  public notifications: JornayaNotification[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private fruHttp: FruHttpService,
    private elRef:ElementRef,
    private jornayaFormService: JornayaFormService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private consentTelemetry: ConsentAttemptTelemetryService,
    ) {
    this.leadValues = leadValues;
  }

  ngOnInit() {
    if (this.isConsentUpdateForm()) {
      //FRU-23038: idempotent — restores the attempt the route/email-verification page
      //already started for this encrypted lead id. Diagnostics only.
      this.consentTelemetry.startAttempt(this.telemetryLeadId(), this.getConsentSource() || 'web');
    }
  	this.buildForm();
  }

  //FRU-23038 diagnostics only — the consent-update flow (context=update, optionally
  //mirrored in by a parent input) reuses the one-to-one consent-attempt telemetry.
  //ConsentAttemptTelemetryService.record swallows its own errors, so telemetry never
  //blocks the form, and no PII ever reaches the payload (the service sanitizes metadata).
  private isConsentUpdateForm(): boolean {
    return this.consentUpdate === true
      || String(this.activatedRoute.snapshot.queryParams['context'] || '') === 'update';
  }

  //The email-verification page stores the encrypted id on lead.campaign; the route id
  //is the fallback when the form is reached some other way.
  private telemetryLeadId(): string {
    const fromLead = String((this.lead && this.lead.campaign) || '');
    if (fromLead) { return fromLead; }
    try {
      return String(this.activatedRoute.snapshot.paramMap.get('id') || '');
    } catch (e) { return ''; }
  }

  private recordConsentEvent(event: ConsentAttemptEventOptions): void {
    if (!this.isConsentUpdateForm()) { return; }
    this.consentTelemetry.record(this.telemetryLeadId(), { ...event, source: 'consent_update' });
  }

  private recordConsentEventOnUnload(event: ConsentAttemptEventOptions): void {
    if (!this.isConsentUpdateForm()) { return; }
    this.consentTelemetry.recordOnUnload(this.telemetryLeadId(), { ...event, source: 'consent_update' });
  }

  buildForm() {
    this.lead.consent_source = this.getConsentSource();

    if (this.isConsentUpdateForm()) {
      this.lead.sms_context = 'update';
    } else if (this.activatedRoute.snapshot.queryParams['context']) {
      this.lead.sms_context = this.activatedRoute.snapshot.queryParams['context'];
    }

    if (this.isConsentUpdateForm()) {
        // add exact value options so the values are not changed to the next closest value range
        // consent update process starts with confirmation of values
        leadValues.purchasePrices.unshift({ key: this.lead.purchasePrice, value: formatCurrency(this.lead.purchasePrice, 'en-US', '$', 'USD', '1.0-0') });
        leadValues.cashOuts.unshift({ key: this.lead.cashOut, value: formatCurrency(this.lead.cashOut, 'en-US', '$', 'USD', '1.0-0') });
        leadValues.propertyValues.unshift({ key: this.lead.propertyValue, value: formatCurrency(this.lead.propertyValue, 'en-US', '$', 'USD', '1.0-0') });
        leadValues.firstMortgageBalances.unshift({ key: this.lead.firstMortgageBalance, value: formatCurrency(this.lead.firstMortgageBalance, 'en-US', '$', 'USD', '1.0-0') });
    }

    // Adjust some numbers that might not exactly match the form dropdown
    let purchasePrice = this.getClosestValue(this.lead.purchasePrice, leadValues.purchasePrices);
    let cashOut = this.getClosestValue(this.lead.cashOut, leadValues.cashOuts);
    let propertyValue = this.getClosestValue(this.lead.propertyValue, leadValues.propertyValues);
    let firstMortgageBalance = this.getClosestValue(this.lead.firstMortgageBalance, leadValues.firstMortgageBalances);

    // Figure out what the buying process dropdown should be showing
    let buyingProcess = 'just_thinking';
    if (this.lead.signedAgreement === 'yes') {
      buyingProcess = 'signed_agreement';
    } else if (this.lead.foundHome === 'yes') {
      buyingProcess = 'found_home';
    } else if (this.lead.daysFromBuying === 'yes') {
      buyingProcess = 'days_from_buying';
    }

    // Set up form
    this.leadForm = this.formBuilder.group({
      rateType: this.formBuilder.control(this.lead.rateType),
      propertyType: this.formBuilder.control(this.lead.propertyType),
      propertyUse: this.formBuilder.control(this.lead.propertyUse),
      creditRating: this.formBuilder.control(this.lead.creditRating),
      zipCode: this.formBuilder.control(this.lead.zipCode, [Validators.required, Validators.pattern(/^(\d{5})$/)]),
      veteranMilitary: this.formBuilder.control(this.lead.veteranMilitary),
      firstName: this.formBuilder.control(this.lead.firstName, [Validators.required, Validators.minLength(2)]),
      lastName: this.formBuilder.control(this.lead.lastName, [Validators.required, Validators.minLength(2)]),
      email: this.formBuilder.control(this.lead.email, [Validators.required, Validators.email]),
      phone: this.formBuilder.control(this.lead.phone, [Validators.required, Validators.pattern(/(\(\d{3}\) \d{3}-\d{4})|\d{10}/)])
    });

    // Type specific fields
    switch(this.lead.type) {
      case 'purchase':
        this.leadForm.addControl('purchasePrice', this.formBuilder.control(purchasePrice));
        this.leadForm.addControl('downPayment', this.formBuilder.control(this.lead.downPayment));
        this.leadForm.addControl('buyingProcess', this.formBuilder.control(buyingProcess));
        this.leadForm.addControl('daysFromBuying', this.formBuilder.control(this.lead.daysFromBuying));
        this.leadForm.addControl('foundHome', this.formBuilder.control(this.lead.foundHome));
        this.leadForm.addControl('signedAgreement', this.formBuilder.control(this.lead.signedAgreement));
        break;
      case 'refinance':
        this.leadForm.addControl('cashOut', this.formBuilder.control(cashOut));
        this.leadForm.addControl('propertyValue', this.formBuilder.control(propertyValue));
        this.leadForm.addControl('firstMortgageBalance', this.formBuilder.control(firstMortgageBalance));
        this.leadForm.addControl('currentFHA', this.formBuilder.control(this.lead.currentFHA));
        this.leadForm.addControl('currentVA', this.formBuilder.control(this.lead.currentVA));
        break;
    }

    // Controls for use in view
    this.buyingProcessControl = this.leadForm.get('buyingProcess');
    this.veteranMilitaryControl = this.leadForm.get('veteranMilitary');
    this.zipCodeControl = this.leadForm.get('zipCode');
    this.firstNameControl = this.leadForm.get('firstName');
    this.lastNameControl = this.leadForm.get('lastName');
    this.emailControl = this.leadForm.get('email');
    this.phoneControl = this.leadForm.get('phone');
  }

  onSubmitForm() {
    if (this.disableSubmit) {
      return;
    }

    this.recordConsentEvent({ stage: 'submit_clicked', outcome: 'success' });

    this.disableSubmit = true;
    this.dismissNotification();

    this.jornayaFormService.waitForTokenOrReport(this.lead.leadId).then(result => {
      if (!result.token) {
        this.recordConsentEvent({ stage: 'jornaya_missing', outcome: 'failure', reason: result.status || 'unknown' });
        this.handleJornayaTokenMissing(result.notification);
        return;
      }

      this.submitForm();
    });
  }

  handleJornayaTokenMissing(notification?: JornayaNotification) {
    if (!notification) {
      return;
    }

    this.notifications = [notification];
    this.disableSubmit = false;
  }

  submitForm() {
    // Get injected elements from tracking scripts
    let trustedForm = this.elRef.nativeElement.querySelector('[name=xxTrustedFormCertUrl]');
    this.lead.trusted_form_cert_url = (trustedForm !== null) ? trustedForm.value : null;
    if (trustedForm === null || !trustedForm.value) {
      this.recordConsentEvent({
        stage: 'trustedform_missing',
        outcome: 'failure',
        reason: trustedForm === null ? 'cert_input_missing' : 'cert_value_missing',
      });
    }

    // const experimentStuff = this.experimentService.forPost();
    // this.lead.experiment = experimentStuff.experiment;
    // this.lead.experiment_variation = experimentStuff.variation;

    const jornayaResult = this.jornayaFormService.getTokenOrReport(this.lead.leadId);
    this.lead.universal_leadid = jornayaResult.token;
    if (!this.lead.universal_leadid) {
      this.recordConsentEvent({ stage: 'jornaya_missing', outcome: 'failure', reason: jornayaResult.status || 'unknown' });
      this.handleJornayaTokenMissing(jornayaResult.notification);
      return;
    }

    // Assign form values
    this.lead = Object.assign(this.lead, this.leadForm.value);

    // Strip things from phone
    this.lead.phone = this.lead.phone.replace(/[^\d]/g, '').slice(0, 10);

    // Submit the lead
    let postLead = new PostLead(this.lead);
    this.fruHttp.submitLead(postLead)
      .subscribe({
        next: response => { this.handleResponse(response); },
        error: error => {
          this.recordConsentEvent({
            stage: 'submit_transport_failed',
            outcome: 'failure',
            reason: (error?.status === 0 || error?.status === undefined) ? 'network' : 'http_error',
            httpStatus: error?.status ?? 0,
          });
          this.disableSubmit = false;
        }
      });
  }

  getClosestValue(goal: number, numbers: any[]) {
    // Assuming 'numbers' will be one of the key/value array of objects for form values
    let values = [];
    for (let k in numbers) {
      values.push(numbers[k].key);
    }

    let closest = values.reduce((prev, curr) => Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev);

    return closest;
  }

  handleResponse(response: FormResponse) {
    // Watch for malformed response
    if (typeof response.status === 'undefined' || !response.status) {
      this.recordConsentEventOnUnload({ stage: 'server_rejected', outcome: 'failure', reason: 'malformed_response' });
      this.fruHttp.redirectOffers(this.lead);
      return;
    }

    switch (response.status) {
      case 'Refresh Required':
        if (response.lead_id) {
          //An SPA swap to the current lead: recorded (not on-unload) exactly like the
          //one-to-one submit-time stale redirect, then the existing navigation runs.
          this.recordConsentEvent({
            stage: 'stale_link_redirect',
            outcome: 'unknown',
            reason: 'refresh_required',
            requestId: String(response.lead_id),
            originalRequestId: this.telemetryLeadId(),
            metadata: { original_encrypted_lead_id: this.telemetryLeadId() },
          });
          this.router.navigate(['/email-verification', response.lead_id], { queryParams: this.activatedRoute.snapshot.queryParams });
          return;
        }
        this.recordConsentEventOnUnload({ stage: 'server_rejected', outcome: 'unknown', reason: 'refresh_required' });
        this.fruHttp.redirectOffers(this.lead);
        break;

      case 'Error':
        if (response.reason === 'ambiguous_consent_update_link') {
          this.recordConsentEvent({
            stage: 'server_rejected',
            outcome: 'failure',
            reason: 'ambiguous_link',
            httpStatus: 200,
          });
          this.notifications = [{
            type: 'danger',
            message: response.message || 'Please refresh this verification link and try again.',
          }];
          this.disableSubmit = false;
          return;
        }
        this.recordConsentEventOnUnload({
          stage: 'server_rejected',
          outcome: 'failure',
          reason: response.reason || 'error',
          httpStatus: 200,
        });
        this.fruHttp.redirectOffers(this.lead);
        break;

      case 'New':
      case 'Sent':
      case 'Block':
      case 'Review':
      case 'Not Sendable':
        // Go to thank you page
        this.recordConsentEventOnUnload({ stage: 'server_accepted', outcome: 'success', httpStatus: 200 });
        this.fruHttp.redirectThankYou(this.lead, response);
        break;

      default:
        // Go to offers page
        this.recordConsentEventOnUnload({
          stage: 'server_rejected',
          outcome: 'failure',
          reason: 'unhandled_status',
          metadata: { response_status: String(response.status) },
        });
        this.fruHttp.redirectOffers(this.lead);
        break;
    }
  }

  getConsentSource(): string {
    const source = String(this.activatedRoute.snapshot.queryParams.src || '').toLowerCase();

    return ['sms', 'email'].includes(source) ? source : '';
  }

  dismissNotification() {
    this.notifications = [];
  }

  buyingProcessChange() {
    switch(this.buyingProcessControl.value) {
      case 'days_from_buying':
        this.leadForm.controls['daysFromBuying'].setValue('yes');
        break;
      case 'found_home':
        this.leadForm.controls['daysFromBuying'].setValue('yes');
        this.leadForm.controls['foundHome'].setValue('yes');
        break;
      case 'signed_agreement':
        this.leadForm.controls['daysFromBuying'].setValue('yes');
        this.leadForm.controls['foundHome'].setValue('yes');
        this.leadForm.controls['signedAgreement'].setValue('yes');
        break;
    }
  }

}
